/**
 * M-05 承認グループ。部門とは切り離して、承認する上長とスタッフの組み合わせを管理する。
 */

import { useState } from 'react';
import { endpoints } from '../../api/endpoints.js';
import type { Employee, GroupView } from '../../api/types.js';
import { Card, EmptyState, ErrorMessage, Kpi, Loading, Notice } from '../../components/ui.js';
import { useAsync } from '../../lib/useAsync.js';

interface EditState {
  id: string | null;
  name: string;
  description: string;
  approverEmployeeId: number | null;
  memberEmployeeIds: number[];
}

const EMPTY_EDIT: EditState = {
  id: null,
  name: '',
  description: '',
  approverEmployeeId: null,
  memberEmployeeIds: [],
};

export function GroupsPage(): React.JSX.Element {
  const state = useAsync(() => endpoints.groups(), []);
  const employees = useAsync(() => endpoints.employees(), []);
  const [edit, setEdit] = useState<EditState | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<unknown>(null);

  const openEdit = (group: GroupView, all: Employee[]): void => {
    const approver = all.find((employee) => employee.id === group.approverEmployeeId);
    setEdit({
      id: group.id,
      name: group.name,
      description: group.description,
      approverEmployeeId: approver?.employeeId ?? null,
      memberEmployeeIds: group.members.map((member) => member.employeeId),
    });
    setActionError(null);
  };

  const save = async (): Promise<void> => {
    if (!edit) return;
    setBusy(true);
    setActionError(null);
    try {
      const payload = {
        name: edit.name,
        description: edit.description,
        approverEmployeeId: edit.approverEmployeeId,
        memberEmployeeIds: edit.memberEmployeeIds,
      };
      if (edit.id === null) await endpoints.createGroup(payload);
      else await endpoints.updateGroup(edit.id, payload);
      setEdit(null);
      state.reload();
      employees.reload();
    } catch (cause) {
      setActionError(cause);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (group: GroupView): Promise<void> => {
    if (group.members.length > 0) {
      setActionError(
        new Error('所属スタッフがいるグループは削除できません。先に割当を変更してください。'),
      );
      return;
    }
    setBusy(true);
    setActionError(null);
    try {
      await endpoints.deleteGroup(group.id);
      state.reload();
    } catch (cause) {
      setActionError(cause);
    } finally {
      setBusy(false);
    }
  };

  if (state.loading && !state.data) return <Loading />;
  if (state.error) return <ErrorMessage error={state.error} />;
  if (!state.data) return <Loading />;

  const all = employees.data?.items ?? [];
  const approverCandidates = all.filter((employee) => employee.role !== 'staff' && employee.active);
  const assigned = state.data.items.reduce((sum, group) => sum + group.members.length, 0);

  return (
    <>
      <Notice tone="info" title="部門長が自動的に承認上長になるわけではありません">
        {state.data.note}
      </Notice>

      <Card title="共通の承認ルート">
        <p className="text-sm">
          スタッフが日報を提出 → グループ上長が確認・承認 → 管理部が月次処理
        </p>
      </Card>

      <div className="grid gap-3 sm:grid-cols-4">
        <Kpi label="承認グループ" value={`${state.data.items.length}件`} />
        <Kpi
          label="上長の設定"
          value={`${state.data.items.filter((group) => group.approverEmployeeId).length}件`}
          note={`未設定 ${state.data.items.filter((group) => !group.approverEmployeeId).length}件`}
        />
        <Kpi label="割当済みスタッフ" value={`${assigned}名`} />
        <Kpi label="未割当" value={`${state.data.unassigned.length}名`} />
      </div>

      <ErrorMessage error={actionError} />

      {state.data.unassigned.length > 0 && (
        <Notice
          tone="warn"
          title={`承認グループが未割当のスタッフが${state.data.unassigned.length}名います`}
        >
          {state.data.unassigned.map((employee) => employee.name).join('、')}
        </Notice>
      )}

      <Card
        title="承認グループ"
        action={
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() => setEdit({ ...EMPTY_EDIT })}
          >
            承認グループを追加
          </button>
        }
      >
        {state.data.items.length === 0 ? (
          <EmptyState message="承認グループがまだありません。" />
        ) : (
          <div className="space-y-3">
            {state.data.items.map((group) => (
              <div key={group.id} className="rounded-xl border border-line p-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p className="font-semibold">{group.name}</p>
                    <p className="text-[0.8rem] text-ink-muted">{group.description}</p>
                    <p className="mt-1 text-sm">
                      上長：
                      {group.approverName ?? <span className="pill pill--warn">未設定</span>}
                      ／所属 {group.memberCount}名
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="btn btn--secondary btn--sm"
                      onClick={() => openEdit(group, all)}
                    >
                      割当を編集
                    </button>
                    <button
                      type="button"
                      className="btn btn--danger btn--sm"
                      disabled={busy}
                      onClick={() => void remove(group)}
                    >
                      削除
                    </button>
                  </div>
                </div>
                {group.members.length > 0 && (
                  <table className="table mt-2">
                    <thead>
                      <tr>
                        <th>社員ID</th>
                        <th>氏名</th>
                        <th>所属部門</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.members.map((member) => (
                        <tr key={member.id}>
                          <td>{member.employeeId}</td>
                          <td>{member.name}</td>
                          <td className="text-ink-muted">{member.department}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      {edit && (
        <Card title={edit.id === null ? '承認グループの追加' : '承認グループの編集'}>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="field-label">グループ名</span>
              <input
                className="input"
                type="text"
                value={edit.name}
                onChange={(event) => setEdit({ ...edit, name: event.target.value })}
              />
            </label>
            <label className="block">
              <span className="field-label">説明</span>
              <input
                className="input"
                type="text"
                value={edit.description}
                onChange={(event) => setEdit({ ...edit, description: event.target.value })}
              />
            </label>
            <label className="block">
              <span className="field-label">上長（1グループに1名）</span>
              <select
                className="input"
                value={edit.approverEmployeeId ?? ''}
                onChange={(event) =>
                  setEdit({
                    ...edit,
                    approverEmployeeId:
                      event.target.value === '' ? null : Number(event.target.value),
                  })
                }
              >
                <option value="">未設定</option>
                {approverCandidates.map((employee) => (
                  <option key={employee.id} value={employee.employeeId}>
                    {employee.employeeId}：{employee.name}（{employee.department}）
                  </option>
                ))}
              </select>
              <span className="field-hint">承認上長・管理部の権限を持つ利用者から選べます</span>
            </label>
          </div>

          <fieldset className="mt-3">
            <legend className="field-label">所属スタッフ</legend>
            <div className="grid max-h-64 gap-1 overflow-auto rounded-lg border border-line p-2 sm:grid-cols-2">
              {all
                .filter((employee) => employee.active)
                .map((employee) => (
                  <label key={employee.id} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={edit.memberEmployeeIds.includes(employee.employeeId)}
                      onChange={(event) =>
                        setEdit({
                          ...edit,
                          memberEmployeeIds: event.target.checked
                            ? [...edit.memberEmployeeIds, employee.employeeId]
                            : edit.memberEmployeeIds.filter((id) => id !== employee.employeeId),
                        })
                      }
                    />
                    {employee.name}
                    <span className="text-[0.75rem] text-ink-muted">{employee.department}</span>
                  </label>
                ))}
            </div>
          </fieldset>

          <div className="mt-3 flex gap-2">
            <button
              type="button"
              className="btn btn--primary"
              disabled={busy || edit.name.trim() === ''}
              onClick={() => void save()}
            >
              保存する
            </button>
            <button type="button" className="btn btn--ghost" onClick={() => setEdit(null)}>
              取消
            </button>
          </div>
        </Card>
      )}
    </>
  );
}
